// Este es el supabase.js que tengo en mi codigo subido a github

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Auth helpers
export const auth = {
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  },

  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  },

  resetPassword: async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { data, error }
  },
}

// Database helpers
export const db = {
  // Categorías
  getCategorias: async (tipo = null) => {
    let query = supabase.from('categorias').select('*').order('nombre')
    if (tipo) {
      query = query.eq('tipo', tipo)
    }
    const { data, error } = await query
    return { data, error }
  },

  // Tiendas/Marcas - Solo publicadas para web pública
  getTiendas: async () => {
    const { data, error } = await supabase
      .from('tiendas')
      .select(`
        *,
        categoria:categorias(*),
        descuentos:descuentos_tiendas(count)
      `)
      .eq('publicado', true)
      .order('nombre')
    return { data, error }
  },

  getTiendaById: async (id) => {
    const { data, error } = await supabase
      .from('tiendas')
      .select(`
        *,
        categoria:categorias(*),
        comentarios(*, publicado),
        descuentos:descuentos_tiendas(*)
      `)
      .eq('id', id)
      .single()
    return { data, error }
  },

  // Eventos - Solo publicados para web pública
  getEventos: async () => {
    const { data, error } = await supabase
      .from('eventos')
      .select(`
        *,
        categoria:categorias(*)
      `)
      .eq('publicado', true)
      .order('fecha_inicio', { ascending: false })
    return { data, error }
  },

  // Eventos para mostrar en Home (máximo 2 vigentes)
  getEventosHome: async () => {
    const { data, error } = await supabase
      .from('eventos')
      .select(`
        *,
        categoria:categorias(*)
      `)
      .eq('publicado', true)
      .in('estado', ['Próximo', 'Activo'])
      .eq('mostrar_en_home', true)
      .order('fecha_inicio', { ascending: false })
      .limit(2)
    return { data, error }
  },

  getEventoById: async (id) => {
    const { data, error } = await supabase
      .from('eventos')
      .select(`
        *,
        categoria:categorias(*),
        eventos_tiendas(
          tiendas!descuentos_marcas_tienda_id_fkey(*)
        )
      `)
      .eq('id', id)
      .single()
    return { data, error }
  },

  // Comentarios
  createComentario: async (comentario) => {
    const { data, error } = await supabase
      .from('comentarios')
      .insert(comentario)
      .select()
      .single()
    return { data, error }
  },

  getComentariosPublicados: async (tiendaId) => {
    const { data, error } = await supabase
      .from('comentarios')
      .select('*')
      .eq('tienda_id', tiendaId)
      .eq('publicado', true)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  // Inscripciones
  createInscripcion: async (inscripcion) => {
    // Verificar si ya está inscrito
    const { data: existing } = await supabase
      .from('inscripciones_eventos')
      .select('id')
      .eq('evento_id', inscripcion.evento_id)
      .eq('usuario_email', inscripcion.usuario_email)
      .single()

    if (existing) {
      return { data: null, error: { message: 'Ya estás inscrito en este evento' } }
    }

    const { data, error } = await supabase
      .from('inscripciones_eventos')
      .insert(inscripcion)
      .select()
      .single()
    return { data, error }
  },

  // Contacto Marcas
  createContactoMarca: async (contacto) => {
    const { data, error } = await supabase
      .from('contacto_marcas')
      .insert(contacto)
      .select()
      .single()
    return { data, error }
  },

  // Postulaciones (Trabaja con nosotros)
  createPostulacion: async (payload) => {
    const { data, error } = await supabase
      .from('postulaciones')
      .insert(payload)
      .select()
      .single()
    return { data, error }
  },

  // SEO Configuration
  getSEOConfig: async (pagina) => {
    const { data, error } = await supabase
      .from('configuracion_seo')
      .select('*')
      .eq('pagina', pagina)
      .single()
    return { data, error }
  },

  updateSEOConfig: async (pagina, config) => {
    const { data, error } = await supabase
      .from('configuracion_seo')
      .upsert({ pagina, ...config, updated_at: new Date().toISOString() })
      .select()
      .single()
    return { data, error }
  },

  // Site Configuration (Organization facts)
  getSiteConfig: async () => {
    const { data, error } = await supabase
      .from('site_config')
      .select('*')
      .limit(1)
      .single()
    return { data, error }
  },

  updateSiteConfig: async (config) => {
    const { data: existing } = await supabase
      .from('site_config')
      .select('id')
      .limit(1)
      .single()
    
    if (existing?.id) {
      const { data, error } = await supabase
        .from('site_config')
        .update({ ...config, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single()
      return { data, error }
    } else {
      const { data, error } = await supabase
        .from('site_config')
        .insert([config])
        .select()
        .single()
      return { data, error }
    }
  },

  // FAQs
  getFAQsByScope: async (scopeType, scopeId = null, pagina = null) => {
    let query = supabase
      .from('faqs')
      .select('*')
      .eq('scope_type', scopeType)
      .eq('publicado', true)
      .order('orden', { ascending: true })
    
    if (scopeType === 'page' && pagina) {
      query = query.eq('pagina', pagina)
    } else if (scopeId) {
      query = query.eq('scope_id', scopeId)
    }
    
    const { data, error } = await query
    return { data, error }
  },

  getAllFAQs: async () => {
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .order('created_at', { ascending: false })
    return { data, error }
  },

  createFAQ: async (faq) => {
    const { data, error } = await supabase
      .from('faqs')
      .insert([faq])
      .select()
      .single()
    return { data, error }
  },

  updateFAQ: async (id, faq) => {
    const { data, error } = await supabase
      .from('faqs')
      .update(faq)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteFAQ: async (id) => {
    const { error } = await supabase
      .from('faqs')
      .delete()
      .eq('id', id)
    return { error }
  },

  // Libro de Reclamaciones
  createReclamo: async (reclamo) => {
    const { data, error } = await supabase
      .from('libro_reclamaciones')
      .insert(reclamo)
      .select()
      .single()
    return { data, error }
  },

  // Sliders - Ya filtra por activo = true
  getSliders: async (seccion = null) => {
    let query = supabase
      .from('slider_home')
      .select('*')
      .eq('activo', true)
      .order('orden')
    
    if (seccion) {
      query = query.eq('seccion', seccion)
    }
    
    const { data, error } = await query
    return { data, error }
  },

  // Contenido Home
  getContenidoHome: async () => {
    const { data, error } = await supabase
      .from('contenido_home')
      .select('*')
    return { data, error }
  },

  // Contenido Legal - Filtra por activo = true Y publicado = true
  getContenidoLegal: async (tipo = null) => {
    let query = supabase
      .from('contenido_legal')
      .select('*')
      .eq('activo', true)
      .eq('publicado', true)
    
    if (tipo) {
      query = query.eq('tipo', tipo).single()
    }
    
    const { data, error } = await query
    return { data, error }
  },

  // Descuentos de Tiendas
  getDescuentosByTienda: async (tiendaId) => {
    const { data, error } = await supabase
      .from('descuentos_tiendas')
      .select('*')
      .eq('tienda_id', tiendaId)
      .eq('activo', true)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  createDescuento: async (descuento) => {
    const { data, error } = await supabase
      .from('descuentos_tiendas')
      .insert(descuento)
      .select()
      .single()
    return { data, error }
  },

  updateDescuento: async (id, descuento) => {
    const { data, error } = await supabase
      .from('descuentos_tiendas')
      .update(descuento)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteDescuento: async (id) => {
    const { data, error } = await supabase
      .from('descuentos_tiendas')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Descuentos (página pública y detalle)
  getDescuentos: async () => {
    const { data, error } = await supabase
      .from('descuentos')
      .select('*')
      .eq('activo', true)
      .eq('publicado', true)
      .order('fecha_inicio', { ascending: false })
    return { data, error }
  },

  getDescuentoById: async (id) => {
    const { data, error } = await supabase
      .from('descuentos')
      .select(`
        *,
        descuentos_marcas(
          id,
          tienda_id,
          brand_name,
          brand_logo_url,
          brand_url,
          created_as_store
        )
      `)
      .eq('id', id)
      .single()
    return { data, error }
  },

  createDescuentoMaster: async (payload) => {
    const { data, error } = await supabase
      .from('descuentos')
      .insert([{
        ...payload,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()
    return { data, error }
  },

  updateDescuentoMaster: async (id, payload) => {
    const { data, error } = await supabase
      .from('descuentos')
      .update({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  upsertDescuentoMarca: async (payload) => {
    const { data, error } = await supabase
      .from('descuentos_marcas')
      .upsert({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .select()
    return { data, error }
  },

  deleteDescuentoMarca: async (id) => {
    const { error } = await supabase
      .from('descuentos_marcas')
      .delete()
      .eq('id', id)
    return { error }
  },

    // Descuentos (página pública y detalle)
  // Listar descuentos publicados y activos
  getDescuentos: async () => {
    const { data, error } = await supabase
      .from('descuentos')
      .select('*')
      .eq('activo', true)
      .eq('publicado', true)
      .order('fecha_inicio', { ascending: false })
    return { data, error }
  },

  // Obtener detalle de un descuento con marcas participantes (tienda o externas)
  getDescuentoById: async (id) => {
    const { data, error } = await supabase
      .from('descuentos')
      .select(`
        *,
        descuentos_marcas(
          id,
          tienda_id,
          brand_name,
          brand_logo_url,
          brand_url,
          created_as_store,
          tiendas!descuentos_marcas_tienda_id_fkey(
            id,
            nombre,
            logo_url,
            slug
          )
        )
      `)
      .eq('id', id)
      .single()
    return { data, error }
  },

  // Admin: crear descuento (tabla master 'descuentos')
  // Nota: nombre distinto para no chocar con createDescuento de 'descuentos_tiendas'
  createDescuentoMaster: async (payload) => {
    const { data, error } = await supabase
      .from('descuentos')
      .insert([{
        ...payload,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()
    return { data, error }
  },

  // Admin: actualizar descuento (tabla master)
  updateDescuentoMaster: async (id, payload) => {
    const { data, error } = await supabase
      .from('descuentos')
      .update({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  // Admin: adjuntar/actualizar marca participante (tienda existente o marca externa)
  upsertDescuentoMarca: async (payload) => {
    const { data, error } = await supabase
      .from('descuentos_marcas')
      .upsert({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .select()
    return { data, error }
  },

  // Admin: eliminar vínculo de marca de un descuento
  deleteDescuentoMarca: async (id) => {
    const { error } = await supabase
      .from('descuentos_marcas')
      .delete()
      .eq('id', id)
    return { error }
  },
  
  // Configuración de Portadas
  getConfiguracionPortada: async (pagina) => {
    const { data, error } = await supabase
      .from('configuracion_portadas')
      .select('*')
      .eq('pagina', pagina)
      .single()
    return { data, error }
  },

  updateConfiguracionPortada: async (pagina, config) => {
    const { data, error } = await supabase
      .from('configuracion_portadas')
      .update(config)
      .eq('pagina', pagina)
      .select()
      .single()
    return { data, error }
  },

  // Ubícanos - Slider
  getUbicanosSlider: async () => {
    const { data, error } = await supabase
      .from('slider_ubicanos')
      .select('*')
      .eq('activo', true)
      .order('orden', { ascending: true })
    return { data, error }
  },

  getAllUbicanosSlider: async () => {
    const { data, error } = await supabase
      .from('slider_ubicanos')
      .select('*')
      .order('orden', { ascending: true })
    return { data, error }
  },

  createUbicanosSlide: async (slide) => {
    const { data, error } = await supabase
      .from('slider_ubicanos')
      .insert(slide)
      .select()
      .single()
    return { data, error }
  },

  updateUbicanosSlide: async (id, slide) => {
    const { data, error } = await supabase
      .from('slider_ubicanos')
      .update(slide)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteUbicanosSlide: async (id) => {
    const { data, error } = await supabase
      .from('slider_ubicanos')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Ubícanos - Cards
  getUbicanosCards: async () => {
    const { data, error } = await supabase
      .from('cards_ubicanos')
      .select('*')
      .eq('activo', true)
      .order('orden', { ascending: true })
    return { data, error }
  },

  getAllUbicanosCards: async () => {
    const { data, error } = await supabase
      .from('cards_ubicanos')
      .select('*')
      .order('orden', { ascending: true })
    return { data, error }
  },

  createUbicanosCard: async (card) => {
    const { data, error } = await supabase
      .from('cards_ubicanos')
      .insert(card)
      .select()
      .single()
    return { data, error }
  },

  updateUbicanosCard: async (id, card) => {
    const { data, error } = await supabase
      .from('cards_ubicanos')
      .update(card)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteUbicanosCard: async (id) => {
    const { data, error } = await supabase
      .from('cards_ubicanos')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Home - Cards ("Explora tiendas" / "Explora eventos")
  getHomeCards: async () => {
    const { data, error } = await supabase
      .from('cards_home')
      .select('*')
      .eq('activo', true)
      .order('orden', { ascending: true })
    return { data, error }
  },

  getAllHomeCards: async () => {
    const { data, error } = await supabase
      .from('cards_home')
      .select('*')
      .order('orden', { ascending: true })
    return { data, error }
  },

  createHomeCard: async (card) => {
    const { data, error } = await supabase
      .from('cards_home')
      .insert(card)
      .select()
      .single()
    return { data, error }
  },

  updateHomeCard: async (id, card) => {
    const { data, error } = await supabase
      .from('cards_home')
      .update(card)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteHomeCard: async (id) => {
    const { data, error } = await supabase
      .from('cards_home')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Home - Bloque negro (imagen, textos y botón)
  getHomeBlock: async () => {
    const { data, error } = await supabase
      .from('bloque_home')
      .select('*')
      .eq('activo', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    return { data, error }
  },

  getAllHomeBlock: async () => {
    const { data, error } = await supabase
      .from('bloque_home')
      .select('*')
      .order('created_at', { ascending: false })
    return { data, error }
  },

  createHomeBlock: async (payload) => {
    const { data, error } = await supabase
      .from('bloque_home')
      .insert(payload)
      .select()
      .single()
    return { data, error }
  },

  updateHomeBlock: async (id, payload) => {
    const { data, error } = await supabase
      .from('bloque_home')
      .update(payload)
      .eq('id', id)
      .select()
      .single()
    return { data, error }
  },

  deleteHomeBlock: async (id) => {
    const { data, error } = await supabase
      .from('bloque_home')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Configuración de Visibilidad de Páginas
  getPageVisibility: async (paginaNombre) => {
    const { data, error } = await supabase
      .from('paginas_configuracion')
      .select('visible')
      .eq('pagina_nombre', paginaNombre)
      .single()
    
    if (error) return { visible: true, error } // Default: visible si hay error
    return { visible: data?.visible ?? true, error: null }
  },

  setPageVisibility: async (paginaNombre, visible) => {
    const { data, error } = await supabase
      .from('paginas_configuracion')
      .update({ 
        visible,
        updated_at: new Date().toISOString()
      })
      .eq('pagina_nombre', paginaNombre)
      .select()
      .single()
    
    return { data, error }
  },
}

// Storage helpers
export const storage = {
  uploadImage: async (bucket, file, path) => {
  // Para documentos legales (reclamaciones y arco): no permitir reemplazo
  const shouldUpsert = bucket !== 'reclamaciones' && bucket !== 'arco'
  
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { 
      contentType: file?.type || 'application/octet-stream', 
      cacheControl: '3600', 
      upsert: shouldUpsert 
    })
  return { data, error }
},

  getPublicUrl: (bucket, path) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    return data.publicUrl
  },

  deleteFile: async (bucket, path) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove([path])
    return { data, error }
  },
}
